import { simpleMix } from '@entities/simple-mix';
import { warehouseModel } from '@entities/warehouse';
import { formatBagId } from '@lib/bag-formatting';
import { Burger } from '@ui/burger-context-menu';
import { ButtonPrimary, ButtonSecondary } from '@ui/button';
import { ModalDialog } from '@ui/modal-dialog';
import { useStore } from 'effector-react';
import { useNavigate } from 'react-router';
import {
  $removeBagConfirmation,
  emptyBag,
  mixBag,
  removeBagCancel,
  removeBagConfirm,
  removeBag,
  unmixBag,
  $emptyBagConfirmation,
  emptyBagCancel,
  emptyBagConfirm,
} from './model';
import {
  $agricultureById,
  editAgricultureModal,
} from '@@/agriculture/store/agriculture.store';
import { IBag } from '@@/bags/ts';

import styles from './styles.module.css';

const { MAX_SIMPLE_MIXING_ITEMS_CNT } = simpleMix;

export function EditBag({
  bag,
  burgerSize = 'big',
}: {
  bag: IBag;
  burgerSize?: 'small' | 'big';
}) {
  const navigate = useNavigate();
  const removeBagConfirmation = useStore($removeBagConfirmation);
  const emptyBagConfirmation = useStore($emptyBagConfirmation);
  const mixedBagsCount = useStore(simpleMix.$mixedBagsCount);
  const isRemovingBagInProgress = useStore(warehouseModel.$isRemovingBag);
  const isClearingBagInProgress = useStore(warehouseModel.$isEmptyingBag);
  const agricultureById = useStore($agricultureById);
  const isMixed = simpleMix.isBagMixed(bag.id);
  const isEmpty = bag.agricultureId === null;
  const bagIdFormatted = formatBagId(bag.id);

  const canMix =
    agricultureById[bag.agricultureId as number]?.mixable &&
    mixedBagsCount < MAX_SIMPLE_MIXING_ITEMS_CNT;

  const onSelect = ({ value }: { value: string; label: string }) => {
    switch (value) {
      case 'remove':
        removeBag({ warehouseId: bag.warehouseId, bagId: bag.id });
        break;
      case 'empty':
        emptyBag({ warehouseId: bag.warehouseId, bagId: bag.id });
        break;
      case 'edit':
        editAgricultureModal({ id: bag.id });
        break;
      case 'mix':
        mixBag({ bagId: bag.id });
        mixedBagsCount === MAX_SIMPLE_MIXING_ITEMS_CNT - 1 &&
          navigate('/mixing-simple');
        break;
      case 'unmix':
        unmixBag({ bagId: bag.id });
        break;
      default:
        break;
    }
  };

  let menuItems = isEmpty
    ? [{ label: 'удалить', value: 'remove' }]
    : [
        ...(canMix ? [{ label: 'замиксовать', value: 'mix' }] : []),

        { label: 'редактировать', value: 'edit' },
        { label: 'опустошить', value: 'empty' },

        ...(isMixed ? [{ label: 'удалить из бункера', value: 'unmix' }] : []),
      ];

  return (
    <>
      <Burger
        buttonClassName={styles[burgerSize]}
        disabled={isClearingBagInProgress || isRemovingBagInProgress}
        onItemSelect={onSelect}
        items={menuItems}
      />

      {removeBagConfirmation[bag.id] && (
        <ModalDialog
          title={`Удалить хранилище ${bagIdFormatted} ?`}
          onClose={() => removeBagCancel()}
          actions={
            <>
              <ButtonPrimary
                disabled={isRemovingBagInProgress}
                label="Удалить"
                onClick={() => removeBagConfirm()}
              />
              <ButtonSecondary label="Отмена" onClick={() => removeBagCancel()} />
            </>
          }
        >
          Отменить действие будет нельзя!
        </ModalDialog>
      )}

      {emptyBagConfirmation[bag.id] && (
        <ModalDialog
          title={`Опустошить хранилище ${bagIdFormatted} ?`}
          onClose={() => emptyBagCancel()}
          actions={
            <>
              <ButtonPrimary
                disabled={isClearingBagInProgress}
                label="Опустошить"
                onClick={() => emptyBagConfirm()}
              />
              <ButtonSecondary label="Отмена" onClick={() => emptyBagCancel()} />
            </>
          }
        >
          Отменить действие будет нельзя!
        </ModalDialog>
      )}
    </>
  );
}
